import {
  getProviderStatusDisplay,
  providerClaimStatusDefinitions as sharedProviderClaimStatusDefinitions,
  type ProviderClaimStatusValue,
} from '@billing/validation/providerClaimStatus';
import {
  CLAIM_TARIFF_STATUS,
  CLAIM_TARIFF_STATUS_BAND,
  claimTariffStatusSchema,
  type ClaimTariffStatusBandValue,
} from '@billing/validation/tariff';
import {
  getStatusChipPalette,
  type StatusChipFamilyKey,
  type StatusChipTone,
} from 'utils/badgeColors';

export { getProviderStatusDisplay } from '@billing/validation/providerClaimStatus';

export interface ProviderStatusPresentation extends StatusChipTone {
  label: string;
  tooltip?: string;
  family: StatusChipFamilyKey;
  band: ClaimTariffStatusBandValue | null;
}

interface ProviderStatusDefinition {
  label: string;
  tooltip?: string;
  family: StatusChipFamilyKey;
  band: ClaimTariffStatusBandValue;
}

export interface ProviderStatusContext {
  hasBeenSubmitted?: boolean;
}

export const providerClaimStatusDefinitions: Record<
  ProviderClaimStatusValue,
  ProviderStatusDefinition
> = {
  [CLAIM_TARIFF_STATUS.INCOMPLETE]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.INCOMPLETE],
    family: 'teal',
    band: CLAIM_TARIFF_STATUS_BAND.PRE_SUBMISSION,
  },
  [CLAIM_TARIFF_STATUS.READY]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.READY],
    family: 'teal',
    band: CLAIM_TARIFF_STATUS_BAND.PRE_SUBMISSION,
  },
  [CLAIM_TARIFF_STATUS.REVERTED]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.REVERTED],
    family: 'teal',
    band: CLAIM_TARIFF_STATUS_BAND.PRE_SUBMISSION,
  },
  [CLAIM_TARIFF_STATUS.SUBMITTED]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.SUBMITTED],
    family: 'purple',
    band: CLAIM_TARIFF_STATUS_BAND.PRE_SUBMISSION,
  },
  [CLAIM_TARIFF_STATUS.RESUBMITTED]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.RESUBMITTED],
    family: 'blue',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.PENDING]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.PENDING],
    family: 'purple',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.QUERIED]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.QUERIED],
    family: 'purple',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.PAYOR_INVOICED]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.PAYOR_INVOICED],
    family: 'purple',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.REJECTED]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.REJECTED],
    family: 'amber',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.UNDERPAID]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.UNDERPAID],
    family: 'amber',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.UNINSURED]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.UNINSURED],
    family: 'amber',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF]: {
    ...sharedProviderClaimStatusDefinitions[
      CLAIM_TARIFF_STATUS.PENDING_WRITE_OFF
    ],
    family: 'amber',
    band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
  },
  [CLAIM_TARIFF_STATUS.UNCLAIMABLE]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.UNCLAIMABLE],
    tooltip: 'This tariff cannot be claimed.',
    family: 'gray',
    band: CLAIM_TARIFF_STATUS_BAND.COMPLETED,
  },
  [CLAIM_TARIFF_STATUS.PAID]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.PAID],
    family: 'green',
    band: CLAIM_TARIFF_STATUS_BAND.COMPLETED,
  },
  [CLAIM_TARIFF_STATUS.OVERPAID]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.OVERPAID],
    family: 'green',
    band: CLAIM_TARIFF_STATUS_BAND.COMPLETED,
  },
  [CLAIM_TARIFF_STATUS.WRITTEN_OFF]: {
    ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.WRITTEN_OFF],
    family: 'gray',
    band: CLAIM_TARIFF_STATUS_BAND.COMPLETED,
  },
};

const submittedReadyDefinition: ProviderStatusDefinition = {
  ...sharedProviderClaimStatusDefinitions[CLAIM_TARIFF_STATUS.READY],
  family: 'blue',
  band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
};

const getProviderStatusDefinition = (
  status: string,
  context: ProviderStatusContext
): ProviderStatusDefinition | undefined => {
  if (
    context.hasBeenSubmitted &&
    (status === CLAIM_TARIFF_STATUS.READY ||
      status === submittedReadyDefinition.label)
  ) {
    return submittedReadyDefinition;
  }

  const parsedStatus = claimTariffStatusSchema.safeParse(status);
  if (!parsedStatus.success) {
    return Object.values(providerClaimStatusDefinitions).find(
      ({ label }) => label === status
    );
  }

  if (
    parsedStatus.data === CLAIM_TARIFF_STATUS.HISTORICAL ||
    parsedStatus.data === CLAIM_TARIFF_STATUS.CANCELLED ||
    parsedStatus.data === CLAIM_TARIFF_STATUS.UNMATCHED
  ) {
    return undefined;
  }

  return providerClaimStatusDefinitions[parsedStatus.data];
};

export const getProviderStatusPresentation = (
  status: string | null | undefined,
  colorScheme: string,
  context: ProviderStatusContext = {}
): ProviderStatusPresentation => {
  const definition = status
    ? getProviderStatusDefinition(status, context)
    : undefined;

  const label = getProviderStatusDisplay(status);
  const family = definition?.family ?? 'gray';

  return {
    label,
    tooltip: definition?.tooltip,
    ...getStatusChipPalette(family, colorScheme),
    family,
    band: definition?.band ?? null,
  };
};
